import React, { useState, useEffect, useCallback } from 'react'
import { ActionIcon, Box, Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { Droppable } from './dragAndDrop/Droppable'
import { Draggable } from './dragAndDrop/Draggable'
import { useDragDropContext } from './dragAndDrop/DragDropContext'
import { FaCaretDown, FaCaretUp } from 'react-icons/fa'
import { ScheduledDriveInterval, getIntervalRemainingSeconds } from '@/utils/scheduling'
import { differenceInSeconds } from 'date-fns'
import { secondsToHHMM, formatInterval, getTimezone } from '@/utils/datetime'
import DriveTypeBadge from './DriveBadge'
import { DailyScheduleResponse, VehicleResponse, RouteResponse } from '@/api/main/types.gen'
import MoveScheduleButton from './MoveScheduleButton'
import { useNavigate } from '@tanstack/react-router'
import { TbCalendarPlus } from 'react-icons/tb'
import { useAuthStore } from '@/stores/auth'
import { hasPermission, PermissionGuard } from '@/utils/permissions'
import { getRouteDisplay } from '@/utils/routes'
import { useTranslation } from 'react-i18next'
import { AutoScheduleButton } from './AutoSchedule/AutoScheduleButton'
import { useCurrentTime } from '@/stores/currentTime'

function DriveBox({
  drive,
  isDragging,
  dragHandleProps,
  onDriveClick,
  routes,
}: {
  drive: ScheduledDriveInterval['drives'][0]
  isDragging: boolean
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void
    onTouchStart: (e: React.TouchEvent) => void
  }
  onDriveClick: (drive: ScheduledDriveInterval['drives'][0]) => void
  routes?: RouteResponse[]
}) {
  const routeDisplay = getRouteDisplay(drive.route_id, routes)
  const estimatedSeconds = drive.estimated_duration || 3600

  // Calculate height multiplier based on estimated duration
  const estimatedHours = estimatedSeconds / 3600
  const heightMultiplier = estimatedHours < 1.5 ? 1 : Math.floor(estimatedHours * 2) / 2 // Round down to nearest 0.5, but keep 1x for < 1.5h
  const baseHeight = 60 // Base height in pixels for 1 hour
  const minHeight = baseHeight * heightMultiplier

  const mouseDownTimeRef = React.useRef<number | null>(null)
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const hadDropZoneRef = React.useRef(false) // Track if a drop zone was ever set during drag
  const { dragState } = useDragDropContext()
  const isThisDriveDragging = dragState.isDragging && dragState.draggedItem?.id === drive.id.toString()
  const prevIsDraggingRef = React.useRef(false)

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTimeRef.current = Date.now()
    hadDropZoneRef.current = false // Reset when starting new drag
    // Clear any pending click timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
    }
    dragHandleProps.onMouseDown(e)
  }

  // Track if a drop zone was set during drag
  React.useEffect(() => {
    if (isThisDriveDragging && dragState.currentDropZone !== null) {
      hadDropZoneRef.current = true
    }
  }, [isThisDriveDragging, dragState.currentDropZone])

  // Watch for when drag ends - if it was a quick click (not a drag), trigger the click handler
  React.useEffect(() => {
    // Check if drag just ended for this drive
    const wasDragging = prevIsDraggingRef.current
    const isDraggingNow = isThisDriveDragging

    if (wasDragging && !isDraggingNow && mouseDownTimeRef.current !== null) {
      // Drag ended - check if it was a click (quick and no drop zone was ever set)
      const timeDiff = Date.now() - mouseDownTimeRef.current
      // If drag ended quickly (less than 500ms) and no drop zone was ever set, it was a click
      if (timeDiff < 500 && !hadDropZoneRef.current) {
        // Small delay to ensure drag state has fully reset
        clickTimeoutRef.current = setTimeout(() => {
          onDriveClick(drive)
          mouseDownTimeRef.current = null
          clickTimeoutRef.current = null
        }, 50)
      } else {
        mouseDownTimeRef.current = null
        hadDropZoneRef.current = false // Reset for next drag
      }
    }

    prevIsDraggingRef.current = isDraggingNow

    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
        clickTimeoutRef.current = null
      }
    }
  }, [isThisDriveDragging, drive, onDriveClick])

  const getBackgroundColor = useCallback((isDragging: boolean, driveState: string | undefined): string => {
    switch (driveState) {
      case 'in_progress':
      case 'finished':
        return 'light-dark(var(--mantine-color-green-1), var(--mantine-color-green-9))'
      case 'cancelled':
      case 'cancelled_in_progress':
        return 'light-dark(var(--mantine-color-red-1), var(--mantine-color-red-9))'
      default:
        return isDragging
          ? 'light-dark(var(--mantine-color-blue-2), var(--mantine-color-blue-8))'
          : 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))'
    }
  }, [])

  const getHoverBackgroundColor = useCallback((driveState: string | undefined): string => {
    switch (driveState) {
      case 'in_progress':
      case 'finished':
        return 'light-dark(var(--mantine-color-green-2), var(--mantine-color-green-8))'
      case 'cancelled':
      case 'cancelled_in_progress':
        return 'light-dark(var(--mantine-color-red-2), var(--mantine-color-red-8))'
      default:
        return 'light-dark(var(--mantine-color-blue-1), var(--mantine-color-dark-5))'
    }
  }, [])

  return (
    <Stack
      onMouseDown={handleMouseDown}
      onTouchStart={dragHandleProps.onTouchStart}
      p="xs"
      style={{
        backgroundColor: getBackgroundColor(isDragging, drive.drive_state),
        boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.3)' : 'none',
        borderBottom: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        opacity: 1,
        transform: 'none',
        transition: isDragging ? undefined : 'all 0.2s ease',
        marginBottom: 0,
        width: '100%',
        boxSizing: 'border-box',
        pointerEvents: isDragging ? 'none' : 'auto',
        cursor: isDragging ? 'grabbing' : 'default',
        borderRadius: '8px',
        position: 'relative',
        minHeight: `${minHeight}px`,
        overflow: 'hidden',
      }}
      miw={100}
      onMouseEnter={e => {
        if (!isDragging) {
          e.currentTarget.style.backgroundColor = getHoverBackgroundColor(drive.drive_state)
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
        }
      }}
      onMouseLeave={e => {
        if (!isDragging) {
          e.currentTarget.style.backgroundColor = getBackgroundColor(isDragging, drive.drive_state)
          e.currentTarget.style.boxShadow = 'none'
        }
      }}
    >
      <Text size="xs" fw={600} c="light-dark(var(--mantine-color-text), #e9ecef)">
        {routeDisplay}
      </Text>
      <Group>
        <Text size="xs" fw={600} c="light-dark(var(--mantine-color-gray-6), #ced4da)">
          {secondsToHHMM(estimatedSeconds)}
        </Text>
      </Group>
      <Box style={{ position: 'absolute', bottom: 0, right: 5, transform: 'translateY(-50%)' }}>
        <DriveTypeBadge driveType={drive.drive_type} />
      </Box>
    </Stack>
  )
}

const DndList = ({
  vehicleId,
  intervalIndex,
  intervals,
  onDriveClick,
  enableDrag,
  routes,
}: {
  vehicleId: string
  intervalIndex: number
  intervals: ScheduledDriveInterval[]
  onDriveClick: (drive: ScheduledDriveInterval['drives'][0]) => void
  enableDrag: boolean
  routes?: RouteResponse[]
}) => {
  const { t } = useTranslation('scheduling')
  const drives = intervals[intervalIndex].drives

  const droppableId = `${vehicleId}#${intervalIndex}`

  // Call hook at the top level, not inside map
  const { dragState } = useDragDropContext()

  return (
    <Droppable droppableId={droppableId}>
      {({ isDraggingOver, dropIndex }) => (
        <div
          style={{
            minHeight: '60px',
            width: '100%',
            boxSizing: 'border-box',
            padding: isDraggingOver ? '8px' : drives.length > 0 ? '4px 0' : '0',
            position: 'relative',
            pointerEvents: 'auto',
            backgroundColor: isDraggingOver
              ? 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-blue-9))'
              : 'transparent',
            borderRadius: isDraggingOver ? '4px' : '0',
            transition: 'all 0.2s ease',
            border: isDraggingOver
              ? '2px dashed light-dark(var(--mantine-color-blue-4), var(--mantine-color-blue-6))'
              : '2px dashed transparent',
            margin: isDraggingOver ? '4px 0' : '0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {drives.map((drive, index) => {
            const draggedItem = dragState.draggedItem
            const isSameDroppable = draggedItem && draggedItem.droppableId === droppableId
            const isDraggedItem = isSameDroppable && draggedItem && draggedItem.id === drive.id.toString()

            // Calculate what index this item has in the filtered list (without dragged item)
            // This is used for showing the placeholder, but the list items stay in their original positions
            let filteredIndex = index
            if (isSameDroppable && draggedItem && dragState.isDragging) {
              const sourceIndex = draggedItem.index
              if (index > sourceIndex) {
                filteredIndex = index - 1
              } else if (index === sourceIndex) {
                // This is the dragged item - it's filtered out for placeholder calculation
                filteredIndex = -1
              }
            }

            // Show placeholder before this item if dropIndex matches
            // Only show placeholder if this is NOT the dragged item
            const showPlaceholderBefore =
              dropIndex !== null && dropIndex === filteredIndex && filteredIndex >= 0 && !isDraggedItem

            return (
              <React.Fragment key={`${drive.id}-${vehicleId}-${intervalIndex}`}>
                {showPlaceholderBefore && (
                  <div
                    style={{
                      height: '3px',
                      backgroundColor: 'var(--mantine-color-blue-6)',
                      borderRadius: '2px',
                      margin: '4px 0',
                      transition: 'all 0.15s ease',
                      boxShadow: '0 0 4px rgba(37, 99, 235, 0.4)',
                    }}
                  />
                )}
                <Draggable id={drive.id.toString()} index={index} droppableId={droppableId} enabled={enableDrag}>
                  {({ isDragging, dragHandleProps }) => {
                    const isCurrentlyDragging = Boolean(isDragging || (isDraggedItem && dragState.isDragging))
                    return (
                      <DriveBox
                        drive={drive}
                        isDragging={isCurrentlyDragging}
                        dragHandleProps={dragHandleProps}
                        onDriveClick={onDriveClick}
                        routes={routes}
                      />
                    )
                  }}
                </Draggable>
              </React.Fragment>
            )
          })}
          {/* Show placeholder at the end if dropIndex is at the end */}
          {(() => {
            const draggedItem = dragState.draggedItem
            const isSameDroppable = draggedItem && draggedItem.droppableId === droppableId
            // dropIndex is in terms of filtered list, so compare with filtered length
            const filteredLength =
              isSameDroppable && draggedItem && dragState.isDragging ? drives.length - 1 : drives.length
            return dropIndex !== null && dropIndex >= filteredLength
          })() && (
            <div
              style={{
                height: '3px',
                backgroundColor: 'var(--mantine-color-blue-6)',
                borderRadius: '2px',
                margin: '4px 0',
                transition: 'all 0.15s ease',
                boxShadow: '0 0 4px rgba(37, 99, 235, 0.4)',
              }}
            />
          )}
          {drives.length === 0 && (
            <div
              style={{
                padding: '8px',
                color: 'gray',
                fontSize: '12px',
                textAlign: 'center',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {t('dropDrivesHere')}
            </div>
          )}
        </div>
      )}
    </Droppable>
  )
}

export function RestDrivesList({
  partitionedIntervals,
  vehicles,
  onDriveClick,
  scheduleForInterval,
  routes,
}: {
  partitionedIntervals: Record<string, { past: ScheduledDriveInterval[]; rest: ScheduledDriveInterval[] }>
  vehicles: Array<VehicleResponse>
  onDriveClick: (drive: ScheduledDriveInterval['drives'][0]) => void
  scheduleForInterval: DailyScheduleResponse
  routes?: RouteResponse[]
}) {
  const { t } = useTranslation('scheduling')
  const { userPermissions } = useAuthStore()
  const navigate = useNavigate()
  const [openedIntervals, setOpenedIntervals] = useState<Record<string, boolean>>({})
  // Kept local on purpose: feeding a ticking time into partitionedIntervals would reset openedIntervals
  const now = useCurrentTime(60_000)

  const toggleInterval = (vehicleId: string, intervalIndex: number) => {
    const key = `${vehicleId}-${intervalIndex}`
    setOpenedIntervals(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  useEffect(() => {
    const initial: Record<string, boolean> = {}
    Object.entries(partitionedIntervals).forEach(([vehicleId, intervals]) => {
      intervals.rest.forEach((_, idx) => {
        initial[`${vehicleId}-${idx}`] = intervals.rest[idx].drives.length > 0
      })
    })
    setOpenedIntervals(initial)
  }, [partitionedIntervals])

  return (
    <Box
      style={{
        gridColumnStart: 1,
        gridColumnEnd: vehicles.length + 1,
      }}
    >
      <Paper shadow="sm" radius="md" bg="transparent" p="unset">
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${vehicles.length}, 1fr)`,
          }}
        >
          {vehicles.map((vehicle, index) => {
            const vehicleId = vehicle.id
            const restIntervals = partitionedIntervals[vehicleId]?.rest || []

            const firstItem = index === 0
            const lastItem = index === vehicles.length - 1
            const vehicleShifts = scheduleForInterval.vehicle_shifts.find(
              shift => shift.vehicle_id === vehicleId
            )?.shifts

            return (
              <Box
                key={vehicleId}
                style={{
                  boxSizing: 'border-box',
                  borderRight: lastItem ? 'none' : '1px solid var(--mantine-color-gray-3)',
                }}
              >
                {restIntervals.length === 0 ? (
                  <Droppable droppableId={`${vehicleId}#empty`}>
                    {({ isDraggingOver }) => (
                      <div
                        style={{
                          minHeight: '200px',
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: isDraggingOver ? '8px' : '0',
                          position: 'relative',
                          pointerEvents: 'auto',
                          backgroundColor: isDraggingOver
                            ? 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-blue-9))'
                            : 'transparent',
                          borderRadius: isDraggingOver ? '4px' : '0',
                          transition: 'all 0.2s ease',
                          border: isDraggingOver
                            ? '2px dashed light-dark(var(--mantine-color-blue-4), var(--mantine-color-blue-6))'
                            : '2px dashed transparent',
                          margin: isDraggingOver ? '4px 0' : '0',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Text c="dimmed" size="sm" p="unset" ta="center">
                          {t('noUpcomingIntervals')}
                        </Text>
                        <Text c="dimmed" size="sm" p="unset" ta="center">
                          {t('dropDrivesHere')}
                        </Text>
                      </div>
                    )}
                  </Droppable>
                ) : (
                  <Stack
                    gap="lg"
                    styles={{
                      root: {
                        paddingLeft: firstItem ? '0px' : 'var(--spacing-xs)',
                        paddingRight: lastItem ? '0px' : 'var(--spacing-xs)',
                        paddingBottom: lastItem ? '0px' : 'var(--spacing-xs)',
                        paddingTop: '0px',
                      },
                    }}
                  >
                    {restIntervals.map((interval, intervalIndex) => {
                      const key = `${vehicleId}-${intervalIndex}`
                      const isOpen = openedIntervals[key] ?? false

                      const intervalDuration = differenceInSeconds(
                        new Date(interval.interval.ending_at),
                        new Date(interval.interval.starting_from)
                      )
                      const intervalRemaining = getIntervalRemainingSeconds(interval.interval, now)

                      const estimatedTime = interval.drives.reduce(
                        (sum, drive) => sum + (drive.estimated_duration || 0),
                        0
                      )

                      const handleAddDrive = (vehicleId: string) => {
                        navigate({
                          to: '/scheduling/$startDate/$endDate/drive/$action/$id',
                          params: {
                            startDate: encodeURIComponent(interval.interval.starting_from),
                            endDate: encodeURIComponent(interval.interval.ending_at),
                            action: 'new',
                            id: vehicleId,
                          },
                        })
                      }

                      return (
                        <Box key={intervalIndex}>
                          <Group justify="space-between" mb="unset">
                            <Group gap="xs">
                              <Tooltip
                                label={isOpen ? t('collapseInterval') : t('expandInterval')}
                                withArrow
                                position="top"
                              >
                                <ActionIcon
                                  variant="subtle"
                                  size="md"
                                  onClick={() => toggleInterval(vehicleId, intervalIndex)}
                                >
                                  {isOpen ? <FaCaretDown /> : <FaCaretUp />}
                                </ActionIcon>
                              </Tooltip>
                              <Text size="sm">{formatInterval(interval.interval, getTimezone())}</Text>
                            </Group>
                          </Group>
                          {isOpen && (
                            <>
                              <DndList
                                vehicleId={vehicleId}
                                intervalIndex={intervalIndex}
                                intervals={restIntervals}
                                onDriveClick={onDriveClick}
                                enableDrag={hasPermission(userPermissions, 'scheduling', 'write')}
                                routes={routes}
                              />
                              <Stack
                                styles={{
                                  root: {
                                    gap: 'unset',
                                  },
                                }}
                              >
                                <Group justify="space-between">
                                  <Text size="xs">
                                    {t('estimatedShort')}: <strong>{secondsToHHMM(estimatedTime)}</strong>
                                  </Text>
                                  {vehicleShifts && (
                                    <PermissionGuard
                                      userPermissions={userPermissions}
                                      resource="scheduling"
                                      action="write"
                                    >
                                      <MoveScheduleButton
                                        vehicles={vehicles}
                                        currentVehicle={vehicle}
                                        interval={interval.interval}
                                        vehicleShifts={vehicleShifts}
                                      />
                                    </PermissionGuard>
                                  )}
                                </Group>
                                <Group justify="space-between">
                                  <Text size="xs">
                                    {t('lengthShort')}: <strong>{secondsToHHMM(intervalDuration)}</strong>
                                  </Text>
                                  <PermissionGuard
                                    userPermissions={userPermissions}
                                    resource="scheduling"
                                    action="write"
                                  >
                                    <Group gap={4} wrap="nowrap">
                                      <AutoScheduleButton
                                        vehicleId={vehicle.id}
                                        intervalStart={interval.interval.starting_from}
                                        intervalEnd={interval.interval.ending_at}
                                        size="sm"
                                      />
                                      <Tooltip label={t('addDrive')}>
                                        <ActionIcon
                                          variant="subtle"
                                          size="sm"
                                          onClick={() => handleAddDrive(vehicle.id)}
                                        >
                                          <TbCalendarPlus />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  </PermissionGuard>
                                </Group>
                                {intervalRemaining !== intervalDuration && (
                                  <Tooltip label={t('remainingInWindow')}>
                                    <Text size="xs" w="fit-content">
                                      {t('remainingShort')}: <strong>{secondsToHHMM(intervalRemaining)}</strong>
                                    </Text>
                                  </Tooltip>
                                )}
                              </Stack>
                            </>
                          )}
                        </Box>
                      )
                    })}
                  </Stack>
                )}
              </Box>
            )
          })}
        </Box>
      </Paper>
    </Box>
  )
}

export default RestDrivesList
